import externalRoutes from '~/utils/externalRoutes';
import { homePath } from '~/utils/routes';

/**
 * Top-level dashboard sections and the permission(s) required to view them.
 * A user must hold at least one of the listed permissions to access the
 * section.
 */
const PERMISSION_GATED_PREFIXES: { prefix: string; permissions: string[] }[] = [
  { prefix: '/products', permissions: ['manage_catalogue'] },
  { prefix: '/enhancements', permissions: ['manage_catalogue'] },
  { prefix: '/discounts', permissions: ['manage_catalogue'] },
  { prefix: '/stores', permissions: ['manage_catalogue'] },
  { prefix: '/fulfilment', permissions: ['manage_fulfilment'] },
  { prefix: '/categories', permissions: ['manage_catalogue'] },
  { prefix: '/orders', permissions: ['view_orders'] },
  { prefix: '/analytics', permissions: ['view_analytics'] },
  { prefix: '/reports', permissions: ['view_logs', 'view_transactions'] },
];

/**
 * Check authentication status.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const auth = useAuthStore();

  // Fetch auth status from the Pinia store
  let { loggedIn } = auth;

  // If the Pinia store does not think we are logged in, double check with the API
  if (!loggedIn) {
    loggedIn = await auth.check();
  }

  let isExternal: boolean;

  // If the current route matches a defined route
  if (to.matched[0]) {
    // Determine if the current route is included in the defined external routes
    isExternal = externalRoutes.includes(to.matched[0].path);
  } else {
    // Otherwise, the route is not external
    isExternal = false;
  }

  // Handle the account query parameter
  const accountSlug = to.query.account as string | undefined;

  // If the account query parameter is present
  if (accountSlug) {
    // Remove the account query parameter from the query object
    const { account, ...restQuery } = to.query;

    // If the user is logged in or the route doesn't require authentication
    if (loggedIn || isExternal) {
      // Remove the account query parameter and navigate to the same route
      return navigateTo({
        path: to.path,
        query: restQuery,
        replace: true,
      });
    }

    // Otherwise, user is not logged in and route requires authentication
    // Create the redirect URL without the account query parameter
    const redirectPath =
      Object.keys(restQuery).length > 0
        ? `${to.path}?${new URLSearchParams(restQuery as Record<string, string>).toString()}`
        : to.path;

    // Redirect to the account-specific login page
    return navigateTo({
      name: 'auth-login-accountSlug',
      params: { accountSlug },
      replace: true,
      query: { redirect: redirectPath },
    });
  }

  // If we are still not logged in and the current route is not an external route
  if (!loggedIn && !isExternal) {
    // Redirect to the login page with the current route as redirect
    return navigateTo({
      name: 'auth-login',
      replace: true,
      query: { redirect: to.fullPath },
    });
  }

  const isInSection = (prefix: string): boolean =>
    to.path === prefix || to.path.startsWith(`${prefix}/`);

  // If the user is logged in and has an account
  if (loggedIn && !isExternal) {
    const accountStore = useAccountStore();

    // If the account is suspended and the user is not on the suspended page
    if (accountStore.isSuspended && to.path !== '/account-suspended') {
      return navigateTo('/account-suspended', { replace: true });
    }

    // If the account is not suspended but the user is on the suspended page
    if (!accountStore.isSuspended && to.path === '/account-suspended') {
      return navigateTo('/', { replace: true });
    }

    // If the account is pending deletion and the user is not on the pending deletion page
    if (
      accountStore.isPendingDeletion &&
      to.path !== '/account-pending-deletion'
    ) {
      return navigateTo('/account-pending-deletion', { replace: true });
    }

    // If the account is not pending deletion but the user is on the pending deletion page
    if (
      !accountStore.isPendingDeletion &&
      to.path === '/account-pending-deletion'
    ) {
      return navigateTo('/', { replace: true });
    }

    // Settings is super-admin only, except bulk vouchers, which admins can
    // reach. Everyone else gets bounced home.
    if (
      isInSection('/settings') &&
      auth.user &&
      auth.role !== 'super_admin' &&
      !(auth.role === 'admin' && isInSection('/settings/bulk-vouchers'))
    ) {
      const target = homePath();

      if (to.path !== target) {
        return navigateTo(target, { replace: true });
      }
    }

    // Section-level permission gates. The user must hold at least one of the
    // permissions listed for the route they're trying to reach. Partner
    // admins (`is_admin`) bypass account-level permission checks.
    if (auth.user && !auth.user.is_admin) {
      const gate = PERMISSION_GATED_PREFIXES.find(({ prefix }) =>
        isInSection(prefix),
      );
      const userPermissions = auth.user.permissions ?? [];

      if (gate && !gate.permissions.some((p) => userPermissions.includes(p))) {
        const target = homePath();

        if (to.path !== target) {
          return navigateTo(target, { replace: true });
        }
      }
    }

    // Fulfilment additionally requires the account to have the feature.
    if (isInSection('/fulfilment') && !accountStore.account?.has_fulfilment) {
      const target = homePath();

      if (to.path !== target) {
        return navigateTo(target, { replace: true });
      }
    }

    // If the account is restricted and the route is a terminal sell route
    if (accountStore.isRestricted && to.path.startsWith('/terminal/sell')) {
      return navigateTo('/terminal', { replace: true });
    }

    // Sell routes require offline-payment permission, or card-payment permission on a card-enabled account; store gateway gaps are handled on the summary.
    if (
      to.path.startsWith('/terminal/sell') &&
      auth.user &&
      !auth.user.is_admin &&
      !auth.user.permissions.includes('process_offline_payments') &&
      (!auth.user.permissions.includes('process_card_payments') ||
        !accountStore.account?.has_terminal_card_payments)
    ) {
      return navigateTo('/terminal', { replace: true });
    }

    // Test sell routes also require process_test_orders.
    if (
      to.path.startsWith('/terminal/sell/test') &&
      auth.user &&
      !auth.user.is_admin &&
      !auth.user.permissions.includes('process_test_orders')
    ) {
      return navigateTo('/terminal', { replace: true });
    }

    // If the account is restricted and the route is the bulk voucher mapping page
    if (
      accountStore.isRestricted &&
      to.path === '/settings/bulk-vouchers/mapping'
    ) {
      return navigateTo('/settings/bulk-vouchers', { replace: true });
    }

    // Store switcher additionally requires the account to have multiple stores.
    // Super-admin reach is already enforced by the /settings gate above.
    if (
      to.path.includes('/settings/store-switchers') &&
      !accountStore.account?.has_multiple_stores
    ) {
      return navigateTo('/settings/general', { replace: true });
    }

    // If the account is restricted and the route is a create route
    if (accountStore.isRestricted && to.path.includes('/create')) {
      // Extract the parent path by removing everything from /create onwards
      let parentPath = to.path.substring(0, to.path.indexOf('/create'));

      // For nested creates (e.g. /products/123/variants/create), the parent
      // path may not have its own page. Redirect to the parent resource instead.
      const nestedCreateSegments = ['/variants', '/group'];

      if (
        nestedCreateSegments.some((segment) => parentPath.endsWith(segment))
      ) {
        parentPath = parentPath.substring(0, parentPath.lastIndexOf('/'));
      }

      return navigateTo(parentPath || '/', { replace: true });
    }
  }
});
